import { useForm } from '@conform-to/react';
import { parse } from '@conform-to/zod';
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  json,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction } from
'@remix-run/node';
import {
  Form,
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useFetchers,
  useLoaderData,
  useMatches,
  useSubmit } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import { useRef } from 'react';
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { ErrorList } from './components/forms.tsx';
import { EpicProgress } from './components/progress-bar.tsx';
import { SearchBar } from './components/search-bar.tsx';
import { useToast } from './components/toaster.tsx';
import { Button } from './components/ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger } from
'./components/ui/dropdown-menu.tsx';
import { Icon, href as iconsHref } from './components/ui/icon.tsx';
import { EpicToaster } from './components/ui/sonner.tsx';
import tailwindStyleSheetUrl from './styles/tailwind.css';
import { getUserId, logout } from './utils/auth.server.ts';
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx';
import { csrf } from './utils/csrf.server.ts';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import { honeypot } from './utils/honeypot.server.ts';
import { combineHeaders, getDomainUrl, getUserImgSrc } from './utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import { useRequestInfo } from './utils/request-info.ts';
import { type Theme, setTheme, getTheme } from './utils/theme.server.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';
import { useOptionalUser, useUser } from './utils/user.ts';

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon-32x32.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' }, (
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const), // necessary to make typescript happy
  //These should match the css preloads above to avoid css as render blocking resource
  { rel: 'icon', type: 'image/png', href: '/favicons/favicon.png' },
  { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
  { title: data ? 'Cougar Corner' : 'Error | Cougar Corner' },
  { name: 'description', content: `BYU Cougars Discussion` }];

};

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root'
  });

  const user = userId ?
  await time(
    () =>
    prisma.user.findUniqueOrThrow({
      select: {
        id: true,
        name: true,
        username: true,
        image: { select: { id: true } },
        roles: {
          select: {
            name: true,
            permissions: {
              select: { entity: true, action: true, access: true }
            }
          }
        }
      },
      where: { id: userId }
    }),
    { timings, type: 'find user', desc: 'find user in root' }
  ) :
  null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' });
  }
  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = honeypot.getInputProps();
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken();

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request)
        }
      },
      ENV: getEnv(),
      toast,
      honeyProps,
      csrfToken
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
        csrfCookieHeader ? { 'set-cookie': csrfCookieHeader } : null
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

const ThemeFormSchema = z.object({
  theme: z.enum(['system', 'light', 'dark'])
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parse(formData, {
    schema: ThemeFormSchema
  });
  if (submission.intent !== 'submit') {
    return json(({ status: 'idle', submission } as const));
  }
  if (!submission.value) {
    return json(({ status: 'error', submission } as const), { status: 400 });
  }
  const { theme } = submission.value;

  const responseInit = {
    headers: { 'set-cookie': setTheme(theme) }
  };
  return json({ success: true, submission }, responseInit);
}

function Document({
  children,
  nonce,
  theme = 'light',
  env = {}





}: {children: React.ReactNode;nonce: string;theme?: Theme;env?: Record<string, string>;}) {
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }} />

				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<LiveReload nonce={nonce} />
			</body>
		</html>);

}

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const user = useOptionalUser();
  const theme = useTheme();
  const matches = useMatches();
  const isOnSearchPage = matches.find((m) => m.id === 'routes/users+/index');
  const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />;
  useToast(data.toast);

  return (
    <Document nonce={nonce} theme={theme} env={data.ENV}>
			<div className="flex h-screen flex-col justify-between">
				<header className="bg-byuRoyal  w-full p-6 text-white">
					<nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
						<Logo />
						<Link to="/">
							<h1 className="text-h1">Cougar Hero</h1>
						</Link>
						<div className="ml-auto hidden max-w-sm flex-1 sm:block">
							{searchBar}
						</div>
						<div className="flex items-center gap-10">
							{user ?
              <UserDropdown /> :

              <Button asChild variant="default" size="lg">
									<Link to="/login">Log In</Link>
								</Button>}

						</div>
						<div className="block w-full sm:hidden">{searchBar}</div>
					</nav>
				</header>

				<div className="flex-1 flex-grow">
					<Outlet />
				</div>

				<footer className="bg-byuNavy w-full p-6 text-white">
					<div className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
						<Logo />
						<div>
							<div className="text-center">© 2021 Cougar Corner, LLC</div>
							<div className="text-center">
								Not affiliated with Brigham Young University or it's associated
								entities. Brigham Young historical logos courtesy of{' '}
								<a
                  href="https://sportslogos.net"
                  target="_blank"
                  rel="noreferrer">

									SportsLogos.net
								</a>
								.
							</div>
						</div>
						<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
					</div>
					{user && user.roles.some((r) => r.name === 'admin') ?
          <div className="text-center">
							<Link to="/admin/updatecfbdata">Update CFB data</Link>
						</div> :

          <div className="text-center">Rise & Shout</div>}

				</footer>
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</Document>);

}

function Logo() {
  return (
    <Link to="/" className="group grid leading-snug">
			{/* <img
        src="/img/CougarCornerMain.com_white_400x67.png"
        alt="Cougar Corner"
        className=""
        /> */}
			<svg
        width="104.915625"
        height="104.915625"
        viewBox="0 0 399 399"
        //class="looka-1j8o68f"
      >
				<defs id="SvgjsDefs1467"></defs>
				<g
          id="SvgjsG1468"
          //featurekey="rootContainer"
          transform="matrix(1.9899455819462468,0,0,1.9899455819462468,0,0)"
          fill="#ffffff">

					<path
            xmlns="http://www.w3.org/2000/svg"
            d="M100.254 0C44.974 0 0 44.974 0 100.254s44.974 100.254 100.254 100.254c55.28 0 100.254-44.974 100.254-100.254S155.534 0 100.254 0zm0 195.508C47.731 195.508 5 152.777 5 100.254S47.731 5 100.254 5s95.254 42.73 95.254 95.254-42.731 95.254-95.254 95.254z">
          </path>
				</g>
				<g
          id="SvgjsG1469"
          //featurekey="symbolFeature-0"
          transform="matrix(1.6117138981509636,0,0,1.6117138981509636,120.89136124669696,64.15854837911525)"
          fill="#ffffff">

					<g xmlns="http://www.w3.org/2000/svg">
						<path d="M58.453,85.914c2.111-6.338,16.9-24.295,26.406-27.464c4.227,10.563,9.508,29.577,6.339,32.745   C88.029,94.365,63.734,88.027,58.453,85.914z"></path>
						<path d="M85.917,58.45c2.112-1.056,5.281-3.169,9.507,1.057c2.112,2.112,4.227,29.576-2.111,30.634   C93.312,84.857,92.255,69.014,85.917,58.45z"></path>
						<path d="M73.627,36.165c-0.084,0.038-0.173,0.067-0.256,0.108c-0.635,0.312-1.234,0.69-1.727,1.13   c-0.24,0.222-0.468,0.449-0.619,0.702c-0.432,0.078-0.846,0.159-1.383,0.448c-0.334-0.473-0.387-1.105-0.365-1.67   c0.3-0.393,0.611-0.747,0.955-1.051c0.654-0.583,1.369-1.026,2.106-1.398c-0.899-1.178-1.881-2.401-2.926-3.65   c-0.257,0.091-0.513,0.183-0.759,0.291c-0.625,0.274-1.219,0.604-1.701,0.995c-0.236,0.197-0.458,0.397-0.604,0.625   c-0.418,0.044-0.824,0.092-1.361,0.36c-0.334-0.458-0.379-1.086-0.344-1.643c0.311-0.375,0.629-0.706,0.979-0.983   c0.69-0.557,1.435-0.957,2.187-1.289c0.049-0.022,0.101-0.037,0.149-0.058c-1.048-1.207-2.158-2.432-3.304-3.66   c-0.483,0.419-0.961,0.908-1.475,1.414l-0.845,0.813c-0.503,0.027-1.024,0.051-1.578-0.093c0.052-0.581,0.104-1.032,0.159-1.501   c0.271-0.214,0.518-0.471,0.78-0.724c0.469-0.461,0.954-0.957,1.503-1.441c-1.258-1.309-2.561-2.612-3.893-3.898   c-0.014,0.007-0.029,0.011-0.041,0.018c-0.558,0.295-1.039,0.673-1.4,1.124c-0.178,0.228-0.342,0.465-0.444,0.735   c-0.409,0.137-0.805,0.319-1.317,0.611c-0.358-0.438-0.523-0.97-0.617-1.509c0.212-0.407,0.441-0.807,0.732-1.16   c0.43-0.536,0.943-0.979,1.494-1.336c-1.3-1.216-2.623-2.404-3.958-3.556c-0.479,0.186-0.914,0.418-1.252,0.709   c-0.233,0.205-0.438,0.422-0.56,0.686c-0.42,0.112-0.83,0.295-1.338,0.667c-0.374-0.459-0.519-1.087-0.523-1.71   c0.287-0.475,0.641-0.906,1.047-1.247c0.276-0.235,0.569-0.424,0.867-0.595c-1.062-0.882-2.127-1.72-3.191-2.536   c-0.487,0.264-0.958,0.556-1.375,0.904c-0.287,0.245-0.567,0.496-0.786,0.786c-0.48,0.084-0.94,0.182-1.537,0.38   c-0.205-0.572-0.167-1.188-0.089-1.761c0.341-0.369,0.684-0.72,1.061-1.028c0.288-0.239,0.596-0.444,0.903-0.649   c-0.821-0.598-1.637-1.161-2.449-1.707c-0.492,0.185-0.963,0.396-1.361,0.664c-0.26,0.181-0.505,0.368-0.674,0.593   c-0.429,0.021-0.847,0.058-1.41,0.293c-0.289-0.498-0.283-1.134-0.194-1.7c0.348-0.363,0.709-0.682,1.096-0.938   c0.146-0.097,0.294-0.183,0.442-0.268c-2.911-1.799-5.716-3.195-8.277-3.995C20.338,9.333,14.529,13.118,2.469,33.099   c2.112,7.394,7.394,29.577,43.308,46.477c2.113-9.508,15.845-26.407,32.747-33.801C78.523,43.855,76.662,40.365,73.627,36.165z"></path>
						<path d="M89.086,94.365C7.75,80.633,1.413,32.043,1.413,32.043s-3.168,25.352,25.352,48.59   C56.341,103.871,95.16,95.391,89.086,94.365z"></path>
						<path d="M1.941,24.649C3.525,14.614,4.318,10.917,8.015,7.396c4.93-4.929,10.563-5.105,20.07-5.633   C14.441,5.107,5.374,16.814,1.941,24.649z"></path>
						<path d="M36.271,2.466c2.397,1.202,4.647,2.429,6.786,3.67c0.441-0.207,0.887-0.376,1.335-0.519   c0.815-0.257,1.637-0.426,2.463-0.541c0.414-0.057,0.828-0.099,1.247-0.125c0.437,0.295,0.858,0.597,1.239,1.031L48.233,7.06   c-0.363,0.022-0.727,0.059-1.088,0.108c-0.59,0.082-1.17,0.207-1.733,0.363c0.818,0.5,1.622,1,2.403,1.503   c0.235-0.127,0.465-0.266,0.709-0.376c0.875-0.389,1.804-0.672,2.787-0.794c0.492-0.059,0.998-0.077,1.51-0.034   c0.484,0.361,0.914,0.776,1.176,1.324c-0.551,0.25-0.977,0.491-1.352,0.781c-0.349-0.029-0.713-0.018-1.078,0.026   c-0.562,0.069-1.128,0.235-1.681,0.442c1.192,0.807,2.345,1.616,3.448,2.426c0.146-0.049,0.291-0.101,0.436-0.143   c0.903-0.26,1.801-0.392,2.686-0.472c0.441-0.039,0.882-0.064,1.318-0.079c0.447,0.306,0.877,0.613,1.299,1.037   c-0.422,0.423-0.834,0.74-1.229,1.075c-0.404,0.014-0.806,0.037-1.203,0.072c-0.386,0.034-0.768,0.082-1.141,0.144   c1.372,1.065,2.686,2.128,3.91,3.186c0.404-0.146,0.816-0.267,1.229-0.361c0.399-0.091,0.803-0.16,1.205-0.211   c0.441,0.268,0.857,0.55,1.245,0.96l-0.983,1.136c-0.276,0.035-0.549,0.086-0.818,0.143c1.263,1.151,2.445,2.293,3.547,3.417   c0.247-0.131,0.501-0.251,0.77-0.348c0.43-0.155,0.879-0.259,1.326-0.309c0.483,0.268,0.9,0.584,1.236,1.036   c-0.406,0.392-0.739,0.71-0.996,1.063c-0.271,0.031-0.531,0.096-0.786,0.183c1.287,1.389,2.468,2.75,3.526,4.063   c0.07-0.019,0.14-0.043,0.211-0.062c0.393-0.098,0.79-0.181,1.193-0.246c0.453,0.251,0.896,0.516,1.291,0.921l-0.955,1.164   c-0.101,0.016-0.201,0.043-0.303,0.062c0.93,1.231,1.766,2.417,2.51,3.542c0.336-0.114,0.677-0.223,1.025-0.307   c0.424-0.101,0.858-0.179,1.307-0.224c0.477,0.275,0.936,0.583,1.281,1.049c-0.437,0.379-0.77,0.703-1.076,1.054   c-0.336,0.033-0.68,0.094-1.021,0.175c-0.113,0.027-0.224,0.069-0.336,0.1c3.22,5.25,4.392,8.894,4.708,9.527   c3.984-1.105,7.917-1.645,11.189,0.891C74.848,7.815,39.969,1.607,36.271,2.466z"></path>
					</g>
				</g>
				<g
          id="SvgjsG1470"
          //featurekey="nameFeature-0"
          transform="matrix(1.060424829227435,0,0,1.060424829227435,100.87915046795761,228.84433031110342)"
          fill="#ffffff">

					<path d="M16.56 40.32 c-4 0 -7.72 -1.56 -10.32 -4.12 c-1.28 -1.28 -2.32 -2.84 -3.08 -4.68 c-0.76 -1.88 -1.16 -3.88 -1.16 -6 c0 -2.16 0.4 -4.2 1.2 -6.08 s1.84 -3.48 3.16 -4.76 c2.68 -2.6 6.4 -4.16 10.32 -4.16 c3.08 0 5.88 0.92 7.76 2.24 c0.96 0.64 1.8 1.4 2.56 2.24 c1.48 1.72 2.28 3.44 2.72 5.12 l-2.92 1.04 c-0.24 -0.96 -0.64 -2 -1.72 -3.52 c-0.56 -0.76 -1.16 -1.44 -1.88 -2.04 s-1.64 -1.12 -2.8 -1.52 c-1.16 -0.44 -2.4 -0.64 -3.8 -0.64 c-3.04 0 -5.96 1.24 -8 3.28 c-2.08 2.08 -3.4 5.16 -3.4 8.6 c0 2.28 0.48 4.36 1.48 6.2 c0.96 1.84 2.32 3.28 4.12 4.32 c1.72 1.04 3.68 1.56 5.84 1.56 c2.68 0 5 -0.92 6.4 -2.04 c0.72 -0.56 1.36 -1.2 1.92 -2 c0.56 -0.76 0.96 -1.44 1.24 -2 c0.24 -0.56 0.48 -1.16 0.68 -1.76 l2.92 1.04 c-0.44 1.56 -1.2 3.16 -2.68 4.96 c-0.72 0.88 -1.6 1.68 -2.52 2.36 c-1.92 1.36 -4.84 2.36 -8.04 2.36 z M63.524 25.36 c0 2.76 -0.64 5.28 -1.88 7.56 c-1.28 2.28 -3.04 4.08 -5.28 5.4 c-2.28 1.32 -4.76 2 -7.56 2 c-2.04 0 -3.96 -0.4 -5.8 -1.16 c-1.8 -0.76 -3.36 -1.8 -4.64 -3.16 c-1.28 -1.28 -2.28 -2.84 -3.04 -4.68 c-0.72 -1.84 -1.12 -3.76 -1.12 -5.8 c0 -2.76 0.64 -5.28 1.92 -7.56 c1.24 -2.28 3 -4.12 5.28 -5.44 c2.24 -1.32 4.76 -1.96 7.52 -1.96 c2.04 0 3.96 0.36 5.8 1.12 c1.8 0.8 3.36 1.84 4.64 3.12 c1.28 1.36 2.32 2.92 3.04 4.72 c0.76 1.84 1.12 3.76 1.12 5.84 z M60.364000000000004 25.52 c0 -3.28 -1.2 -6.32 -3.2 -8.52 s-5 -3.6 -8.36 -3.6 c-2.16 0 -4.12 0.56 -5.88 1.6 c-1.76 1.12 -3.12 2.56 -4.08 4.36 c-0.96 1.84 -1.44 3.84 -1.44 6 c0 3.24 1.2 6.32 3.2 8.48 c2 2.2 5 3.6 8.32 3.6 c2.16 0 4.12 -0.56 5.88 -1.64 c1.8 -1.04 3.16 -2.52 4.12 -4.32 s1.44 -3.8 1.44 -5.96 z M89.008 10.84 l3.08 0 l0 17.76 c0 3.68 -1 6.56 -2.88 8.6 c-1.92 2.04 -4.64 3.08 -8.12 3.08 c-3.44 0 -6.16 -1.04 -8.04 -3.12 c-1.92 -2.08 -2.88 -4.84 -2.88 -8.36 l0 -17.96 l3.04 0 l0 17.8 c0 3.08 0.88 5.44 2.32 6.76 c1.48 1.36 3.36 2 5.72 2 c2.48 0 4.4 -0.72 5.76 -2.24 c1.32 -1.52 2 -3.6 2 -6.36 l0 -17.96 z M126.25200000000001 19.2 l-2.92 1.04 c-0.36 -1.04 -0.84 -2.12 -2 -3.44 c-0.56 -0.6 -1.2 -1.2 -1.92 -1.68 c-1.44 -1 -3.68 -1.72 -6.12 -1.72 c-2.2 0 -4.16 0.52 -5.92 1.64 s-3.12 2.56 -4.08 4.4 c-0.96 1.8 -1.44 3.76 -1.44 5.92 c0 3.28 1.12 6.32 3.04 8.52 c1.96 2.2 5 3.56 8.44 3.56 c2.6 0 5.08 -0.84 6.96 -2.28 s3.16 -3.76 3.32 -6.44 l-12.16 0 l0 -2.84 l15.44 0 l0 1.12 c0 3.68 -1.32 7 -3.6 9.4 c-2.32 2.4 -5.88 3.88 -10 3.88 c-4.36 0 -8.16 -1.6 -10.64 -4.24 s-3.96 -6.4 -3.96 -10.52 c0 -2.76 0.64 -5.32 1.92 -7.6 s3.04 -4.08 5.28 -5.4 s4.76 -1.96 7.48 -1.96 c3.44 0 6.48 0.96 8.56 2.64 c2.12 1.64 3.52 3.64 4.32 6 z M155.09600000000003 40 l-3.2 -7.44 l-15.88 0 l-3.2 7.44 l-3.12 0 l12.56 -29.16 l3.48 0 l12.6 29.16 l-3.24 0 z M150.73600000000002 29.759999999999998 l-6.8 -15.76 l-6.76 15.76 l13.56 0 z M184.82000000000002 40 l-3.72 0 l-8.64 -11.8 l-5.92 0 l0 11.8 l-3.08 0 l0 -29.16 l10.12 0 c3.44 0 6.2 0.96 7.8 2.56 s2.44 3.6 2.44 5.96 c0 4.92 -3.68 7.76 -8 8.32 z M166.54000000000002 13.719999999999999 l0 11.68 l6.76 0 c2.56 0 4.56 -0.72 5.72 -1.88 c1.12 -1.12 1.68 -2.52 1.68 -4.08 c0 -2.88 -1.76 -5.72 -7.32 -5.72 l-6.84 0 z"></path>
				</g>
				<g
          id="SvgjsG1471"
          //featurekey="nameFeature-1"
          transform="matrix(1.7227711143659408,0,0,1.7227711143659408,96.04000640664266,263.80752973754187)"
          fill="#ffffff">

					<path d="M23.72 40 l0 -13.36 l-16.6 0 l0 13.36 l-3.08 0 l0 -29.16 l3.08 0 l0 12.92 l16.6 0 l0 -12.92 l3.08 0 l0 29.16 l-3.08 0 z M35.844 40 l0 -29.16 l18.24 0 l0 2.84 l-15.16 0 l0 10.08 l13.88 0 l0 2.84 l-13.88 0 l0 10.56 l15.32 0 l0 2.84 l-18.4 0 z M82.888 40 l-3.72 0 l-8.64 -11.8 l-5.92 0 l0 11.8 l-3.08 0 l0 -29.16 l10.12 0 c3.44 0 6.2 0.96 7.8 2.56 s2.44 3.6 2.44 5.96 c0 4.92 -3.68 7.76 -8 8.32 z M64.608 13.719999999999999 l0 11.68 l6.76 0 c2.56 0 4.56 -0.72 5.72 -1.88 c1.12 -1.12 1.68 -2.52 1.68 -4.08 c0 -2.88 -1.76 -5.72 -7.32 -5.72 l-6.84 0 z M116.572 25.36 c0 2.76 -0.64 5.28 -1.88 7.56 c-1.28 2.28 -3.04 4.08 -5.28 5.4 c-2.28 1.32 -4.76 2 -7.56 2 c-2.04 0 -3.96 -0.4 -5.8 -1.16 c-1.8 -0.76 -3.36 -1.8 -4.64 -3.16 c-1.28 -1.28 -2.28 -2.84 -3.04 -4.68 c-0.72 -1.84 -1.12 -3.76 -1.12 -5.8 c0 -2.76 0.64 -5.28 1.92 -7.56 c1.24 -2.28 3 -4.12 5.28 -5.44 c2.24 -1.32 4.76 -1.96 7.52 -1.96 c2.04 0 3.96 0.36 5.8 1.12 c1.8 0.8 3.36 1.84 4.64 3.12 c1.28 1.36 2.32 2.92 3.04 4.72 c0.76 1.84 1.12 3.76 1.12 5.84 z M113.41199999999999 25.52 c0 -3.28 -1.2 -6.32 -3.2 -8.52 s-5 -3.6 -8.36 -3.6 c-2.16 0 -4.12 0.56 -5.88 1.6 c-1.76 1.12 -3.12 2.56 -4.08 4.36 c-0.96 1.84 -1.44 3.84 -1.44 6 c0 3.24 1.2 6.32 3.2 8.48 c2 2.2 5 3.6 8.32 3.6 c2.16 0 4.12 -0.56 5.88 -1.64 c1.8 -1.04 3.16 -2.52 4.12 -4.32 s1.44 -3.8 1.44 -5.96 z"></path>
				</g>
			</svg>
		</Link>);

}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <AuthenticityTokenProvider token={data.csrfToken}>
			<HoneypotProvider {...data.honeyProps}>
				<App />
			</HoneypotProvider>
		</AuthenticityTokenProvider>);

}

export default withSentry(AppWithProviders);

function UserDropdown() {
  const user = useUser();
  const submit = useSubmit();
  const formRef = useRef<HTMLFormElement>(null);
  return (
    <DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
            to={`/users/${user.username}`}
            // this is for progressive enhancement
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2">

						<img
              className="h-8 w-8 rounded-full object-cover"
              alt={user.name ?? user.username}
              src={getUserImgSrc(user.image?.id)} />

						<span className="text-body-sm font-bold">
							{user.name ?? user.username}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}/notes`}>
							<Icon className="text-body-md" name="pencil-2">
								Notes
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
            asChild
            // this prevents the menu from closing before the form submission is completed
            onSelect={(event) => {
              event.preventDefault();
              submit(formRef.current);
            }}>

						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>);

}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
  const hints = useHints();
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeMode();
  if (optimisticMode) {
    return optimisticMode === 'system' ? hints.theme : optimisticMode;
  }
  return requestInfo.userPrefs.theme ?? hints.theme;
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find((f) => f.formAction === '/');

  if (themeFetcher && themeFetcher.formData) {
    const submission = parse(themeFetcher.formData, {
      schema: ThemeFormSchema
    });
    return submission.value?.theme;
  }
}

function ThemeSwitch({ userPreference }: {userPreference?: Theme | null;}) {
  const fetcher = useFetcher<typeof action>();

  const [form] = useForm({
    id: 'theme-switch',
    lastSubmission: fetcher.data?.submission
  });

  const optimisticMode = useOptimisticThemeMode();
  const mode = optimisticMode ?? userPreference ?? 'system';
  const nextMode =
  mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system';
  const modeLabel = {
    light:
    <Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>,

    dark:
    <Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>,

    system:
    <Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>

  };

  return (
    <fetcher.Form method="POST" {...form.props}>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<button
          type="submit"
          className="flex h-8 w-8 cursor-pointer items-center justify-center">

					{modeLabel[mode]}
				</button>
			</div>
			<ErrorList errors={form.errors} id={form.errorId} />
		</fetcher.Form>);

}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>);

}